/**
 * Tiered disclosure on the PUBLIC break-glass verify endpoints
 * (`/molst/verify/:qrToken`, `/directives/verify/:qrToken`).
 *
 * A registered device (valid `X-Device-Token` header) gets the full document
 * plus `tier: 'full'`; everyone else gets this minimal emergency summary.
 * Legacy responses without a `tier` field are treated as full.
 */
/**
 * Whether the scanned document still governs care. A printed card outlives the
 * document it was printed from, so this is the first thing a responder must
 * read — before any wish on the card.
 */
export type MinimalDocumentState = 'current' | 'superseded' | 'revoked' | 'withdrawn' | 'not_yet_active';

export interface MinimalVerifyResponse {
  tier: 'minimal';
  // Optional in the type only so a response from an older backend still parses;
  // the card treats a missing value as "state unknown", never as current.
  documentState?: MinimalDocumentState;
  isCurrentVersion?: boolean;
  patientName: string;
  codeStatus: {
    cpr: 'yes' | 'no' | 'unsure' | null;
    source: string | null;
  };
  blockchain: {
    verified: boolean;
    txId: string | null;
  };
  agent: { name: string; phone: string | null } | null;
  // 'local_only' = content matches the locally stored hash, but the ledger
  // could not confirm it (unreachable or no anchor) — honest, not "verified".
  integrityResult: 'pass' | 'fail' | 'local_only' | 'unknown';
  /**
   * The clinical facts that change treatment in the first five minutes.
   * Null when the patient has recorded none — an empty section would read as
   * "none known", which is a different and dangerous claim.
   */
  emergencyInfo?: EmergencyInfoSummary | null;
}

export interface EmergencyInfoSummary {
  allergies: string[];
  bloodThinners: string[];
  implantedDevices: string[];
  conditions: string[];
  bloodType: string | null;
  preferredLanguage: string | null;
  interpreterNeeded: boolean;
}

export function isMinimalVerifyResponse(response: unknown): response is MinimalVerifyResponse {
  return !!response && (response as MinimalVerifyResponse).tier === 'minimal';
}
